import type { HostProvider } from '@tasks/bridge-client/host';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '#components/ui/button';
import { useAuth } from '#features/auth/auth-context';
import { loginWithVitto } from '#lib/auth/auth-api';
import { authSession, type AuthSession } from '#lib/auth/auth-session';
import { vittoSession } from '#lib/auth/vitto-session';
import { type DoorFailure, enterWithVitto } from '#lib/auth/vitto-door';
import { doorMessage } from './door-copy.js';

type Phase = { phase: 'entering' } | { phase: 'failed'; failure: DoorFailure } | { phase: 'open' };

/** Does THIS URL carry what Vitto sends back after «Continue with Vitto»? (`#vitto-return=code&code=…&state=…`; the host module validates it all.) */
export const hasVittoReturn = (hash: string): boolean =>
  new URLSearchParams(hash.replace(/^#/, '')).has('vitto-return');

const Frame = ({ children }: { children: ReactNode }) => (
  <main className="grid min-h-dvh place-items-center bg-background px-6">
    <div className="flex max-w-sm flex-col items-center gap-3 text-center">{children}</div>
  </main>
);

/**
 * TASKS-APP-FULL-UI-1 U2b-2 — the return of «Continue with Vitto» in a TOP-LEVEL tab. Vitto sent the person back with a one-time code in the
 * hash: it is redeemed for a pass (`host.grants('open')`) and the pass traded for the product session (`enterWithVitto`), BEFORE the router
 * mounts — otherwise the first route would see an anonymous visitor and send them to the login, dropping the hash. With no return in the URL
 * this gate is invisible: the product's own login (and its button) is what the person sees.
 */
export const StandaloneGate = ({
  host,
  session = authSession,
  exchange = loginWithVitto,
  hash = () => globalThis.location.hash,
  children,
}: {
  host: HostProvider;
  session?: AuthSession;
  exchange?: (grant: string) => Promise<unknown>;
  hash?: () => string;
  children: ReactNode;
}) => {
  // read ONCE, at the first render: `redeem` strips the hash from the address bar as soon as it starts
  const returning = useRef(hasVittoReturn(hash()));
  const [state, setState] = useState<Phase>(
    returning.current ? { phase: 'entering' } : { phase: 'open' },
  );
  const running = useRef(false);
  // The stored session (if any) is being rotated by the boot: redeeming only AFTER it settles keeps an old refresh token from landing on top of the new pair.
  const booted = useAuth().state !== 'unknown';

  const run = useCallback(() => {
    if (running.current) return;
    running.current = true;
    setState({ phase: 'entering' });
    void enterWithVitto({
      host,
      exchange,
      accept: (pair) => session.accept(pair),
      reason: 'open',
    }).then((outcome) => {
      running.current = false;
      if (outcome.ok) vittoSession.set({ identity: outcome.identity, label: host.viewerLabel?.() });
      setState(outcome.ok ? { phase: 'open' } : { phase: 'failed', failure: outcome.failure });
    });
  }, [host, exchange, session]);

  // StrictMode runs this effect twice on the same instance: `running` keeps ONE redemption (a code is single-use)
  useEffect(() => {
    if (returning.current && booted) run();
  }, [run, booted]);

  if (state.phase === 'entering')
    return (
      <Frame>
        <div role="status">
          <h1 className="text-base font-semibold">Signing in with Vitto…</h1>
        </div>
      </Frame>
    );

  if (state.phase === 'failed') {
    const message = doorMessage(state.failure);
    return (
      <Frame>
        <div role="alert" className="flex flex-col items-center gap-3">
          <h1 className="text-base font-semibold">{message.title}</h1>
          <p className="text-sm text-muted-foreground">{message.detail}</p>
          {/* a redemption spends the code: «try again» is a NEW round trip to Vitto, never a second redeem. Without `connect` there is nothing to offer (T1 of GABO, O2) */}
          {host.connect === undefined ? null : (
            <Button type="button" onClick={() => void host.connect?.()}>
              Continue with Vitto
            </Button>
          )}
          <Button type="button" variant="link" onClick={() => setState({ phase: 'open' })}>
            Use your password instead
          </Button>
        </div>
      </Frame>
    );
  }

  return children;
};
